import { captureException } from './analytics/posthog';
import { isBot } from './analytics/bot-detection';

const API_URL = process.env.NEXT_PUBLIC_CYCLONE_API_URL || 'https://cyclone.agora.red/v1';

const CHUNK_ERROR_PATTERNS = [
  'failed to fetch dynamically imported module',
  'importing a module script failed',
  'chunkloaderror',
  'loading chunk',
  'is not a valid javascript mime type',
  '_next/static',
  'failed to load',
];

export function isChunkLoadError(message) {
  const lower = (message || '').toLowerCase();
  return CHUNK_ERROR_PATTERNS.some((p) => lower.includes(p));
}

// Una pestaña que quedó abierta desde antes de un deploy sigue corriendo los chunks del build
// anterior y, cuando se mezclan con los nuevos, falla con errores sin la palabra "chunk"
// (`(0,i.useFullScreenSearch) is not a function`). Se reconoce porque el chunk del stack ya
// no existe en el sitio: Cloudflare solo sirve los assets del último deploy.
const STALE_RELOAD_KEY = 'lex:stale-build-reload';

// No alcanza con el primer chunk: los que no cambiaron entre builds conservan el hash y
// siguen dando 200, así que se prueban todos los propios del stack (sin repetir, hasta 6) en
// paralelo. Cualquier 404 = la pestaña es de un build viejo.
const MAX_PROBED_CHUNKS = 6;
const PROBE_TIMEOUT_MS = 3000;

function appChunkUrls(stack) {
  if (typeof window === 'undefined' || !stack) return [];
  const urls = stack.match(/https?:\/\/[^\s)]+?\/_next\/static\/[^\s):]+\.js/g) || [];
  return [...new Set(urls.filter((u) => isSameOriginUrl(u)))].slice(0, MAX_PROBED_CHUNKS);
}

async function isMissingChunk(url) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  try {
    const r = await fetch(url, { method: 'HEAD', cache: 'no-store', signal: controller.signal });
    return r.status === 404;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

async function isFromStaleBuild(stack) {
  const urls = appChunkUrls(stack);
  if (!urls.length) return false;
  const missing = await Promise.all(urls.map(isMissingChunk));
  return missing.includes(true);
}

// Recarga una sola vez por pestaña cada 5 minutos, así un chunk roto de verdad no queda en loop.
export async function reloadIfStaleBuild(stack) {
  if (!(await isFromStaleBuild(stack))) return false;
  try {
    const last = Number(sessionStorage.getItem(STALE_RELOAD_KEY) || 0);
    if (Date.now() - last < 5 * 60 * 1000) return false;
    sessionStorage.setItem(STALE_RELOAD_KEY, String(Date.now()));
  } catch {
    // Sin sessionStorage recargamos igual: es un error del build viejo.
  }
  window.location.reload();
  return true;
}

// Browser-extension / non-app source markers. Errors whose stack or source
// filename originate from one of these come from injected content scripts
// (translators, password managers, accessibility tools) — not our code — and
// are pure noise in #incidents.
const EXTENSION_SOURCE_PATTERNS = [
  'chrome-extension://',
  'moz-extension://',
  'safari-extension://',
  'safari-web-extension://',
  'webkit-masked-url',
];

// Returns true when the given text (a stack and/or source filename) clearly
// originates from a browser extension rather than the app bundle.
export function isExtensionError(text) {
  const lower = (text || '').toLowerCase();
  return EXTENSION_SOURCE_PATTERNS.some((p) => lower.includes(p));
}

// Returns true when a stack has no usable app frame: either there is no stack
// at all, or it only contains anonymous (`<anonymous>`) frames. Such errors
// can't be attributed to our code and are dropped.
function hasNoUsableAppFrame(stack) {
  if (!stack || stack === 'N/A') return true;
  const frames = stack
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.startsWith('at ') || l.includes('@'));
  if (frames.length === 0) return true;
  return frames.every((l) => l.includes('<anonymous>'));
}

function identifyRouteContext(url) {
  if (!url) return 'Unknown route';
  try {
    const pathname = new URL(url).pathname;
    if (pathname === '/') return 'Home page';
    if (pathname.startsWith('/signup')) return 'Signup page';
    if (pathname.startsWith('/signin')) return 'Signin page';
    if (pathname.startsWith('/callback')) return 'Auth callback';
    if (pathname.startsWith('/onboarding')) return 'Onboarding';
    if (pathname.startsWith('/perfil')) return 'Profile page';
    if (pathname.startsWith('/negocios')) return `Directory: ${pathname}`;
    if (pathname.startsWith('/directorio')) return `Business listing: ${pathname}`;
    if (pathname.startsWith('/servicios')) return `Service page: ${pathname}`;
    const categoryMatch = pathname.match(/^\/([^/]+)/);
    if (categoryMatch) return `Category: ${categoryMatch[1]}`;
    return `Route: ${pathname}`;
  } catch {
    return 'Unknown route';
  }
}

const REACT_ERRORS = {
  '#185': 'Maximum update depth exceeded (infinite loop)',
  '#301': 'Invalid hook call',
  '#418': 'Hydration failed (server/client mismatch)',
  '#423': 'Hydration text mismatch',
  '#425': 'Hydration tag mismatch',
  '#421': 'Component suspended while hydrating',
  '#31': 'Objects not valid as React child',
};

function inferCause(message) {
  const msg = message.toLowerCase();
  const reactMatch = message.match(/react error #(\d+)/i);
  if (reactMatch) return REACT_ERRORS[`#${reactMatch[1]}`] || `React error #${reactMatch[1]}`;
  if (msg.includes('chunk') || msg.includes('_next/static') || msg.includes('module')) return 'Stale JS (needs refresh)';
  if (msg.includes('hydration')) return 'Hydration mismatch';
  if (msg.includes('cannot read') || msg.includes("reading '")) return 'Null/undefined access';
  if (msg.includes('cannot destructure')) return 'Destructure from undefined';
  if (msg.includes('fetch') || msg.includes('network')) return 'Network/API error';
  if (msg.includes('timeout')) return 'Timeout';
  return 'Unknown';
}

export function formatError(error) {
  if (!error) return 'Unknown error';
  if (typeof error === 'string') return error;
  if (error instanceof Error) {
    return `${error.message}${error.stack ? `\n${error.stack.split('\n').slice(0, 4).join('\n')}` : ''}`;
  }
  try {
    return JSON.stringify(error).substring(0, 500);
  } catch {
    return 'Non-serializable error';
  }
}

// --- Source map symbolication (matches Nightwing's reportError.ts) ---

const STACK_LINE_WITH_FUNC = /^\s*at\s+(.+?)\s+\((https?:\/\/[^)]+):(\d+):(\d+)\)$/;
const STACK_LINE_NO_FUNC = /^\s*at\s+(https?:\/\/\S+):(\d+):(\d+)$/;
const STACK_LINE_SAFARI = /^(.*?)@(https?:\/\/\S+):(\d+):(\d+)$/;
const SOURCE_MAPPING_URL_RE = /[#@]\s*sourceMappingURL\s*=\s*([^\s]+)/;
const SOURCE_MAP_CACHE = new Map();

function isSameOriginUrl(url) {
  if (typeof window === 'undefined') return false;
  try { return new URL(url).origin === window.location.origin; } catch { return false; }
}

function normalizeSourcePath(source) {
  return source
    .replace(/^turbopack:\/\/\/\[project\]\//, '')
    .replace(/^turbopack:\/\/\/?/, '')
    .replace(/^webpack:\/\/_N_E\//, '')
    .replace(/^webpack:\/\/\//, '')
    .replace(/^webpack:\/\//, '')
    .replace(/^\.\//, '');
}

function parseStackLine(line) {
  let m = line.match(STACK_LINE_WITH_FUNC);
  if (m) return { functionName: m[1], fileUrl: m[2], line: +m[3], column: +m[4] };
  m = line.match(STACK_LINE_NO_FUNC);
  if (m) return { functionName: null, fileUrl: m[1], line: +m[2], column: +m[3] };
  m = line.match(STACK_LINE_SAFARI);
  if (m) return { functionName: m[1] || null, fileUrl: m[2], line: +m[3], column: +m[4] };
  return null;
}

async function resolveSourceMapUrl(scriptUrl) {
  const mapUrl = scriptUrl.split('?')[0].split('#')[0] + '.map';
  try {
    const r = await fetch(mapUrl, { method: 'HEAD', cache: 'force-cache' });
    if (r.ok) return mapUrl;
  } catch {}
  try {
    const r = await fetch(scriptUrl, { cache: 'force-cache' });
    if (!r.ok) return null;
    const text = await r.text();
    const match = text.match(SOURCE_MAPPING_URL_RE);
    if (!match || match[1].startsWith('data:')) return null;
    return new URL(match[1].trim(), scriptUrl).toString();
  } catch { return null; }
}

async function getSourceMapConsumer(scriptUrl) {
  if (SOURCE_MAP_CACHE.has(scriptUrl)) return SOURCE_MAP_CACHE.get(scriptUrl);
  const promise = (async () => {
    try {
      const mapUrl = await resolveSourceMapUrl(scriptUrl);
      if (!mapUrl) return null;
      const r = await fetch(mapUrl, { cache: 'force-cache' });
      if (!r.ok) return null;
      const rawMap = await r.json();
      const { SourceMapConsumer } = await import('source-map-js');
      return new SourceMapConsumer(rawMap);
    } catch { return null; }
  })();
  SOURCE_MAP_CACHE.set(scriptUrl, promise);
  return promise;
}

async function symbolicateStackLine(line) {
  const parsed = parseStackLine(line);
  if (!parsed?.fileUrl || !parsed.line || !isSameOriginUrl(parsed.fileUrl)) return line;
  const consumer = await getSourceMapConsumer(parsed.fileUrl);
  if (!consumer) return line;
  const original = consumer.originalPositionFor({ line: parsed.line, column: Math.max(0, parsed.column - 1) });
  if (!original.source || original.line == null) return line;
  const src = normalizeSourcePath(original.source);
  const fn = parsed.functionName || original.name;
  return fn ? `    at ${fn} (${src}:${original.line}:${(original.column || 0) + 1})` : `    at ${src}:${original.line}:${(original.column || 0) + 1}`;
}

async function symbolicateStack(stack) {
  if (!stack || stack === 'N/A') return { stack, symbolicated: false, appFrame: null };
  const lines = stack.split('\n');
  const results = await Promise.all(lines.map(async (l) => {
    const mapped = await symbolicateStackLine(l);
    return { mapped, changed: mapped !== l };
  }));
  const mappedLines = results.map((r) => r.mapped);
  const appFrame = mappedLines.find((l) => l.trim().startsWith('at ') && l.includes('app/') && !l.includes('node_modules'));
  return { stack: mappedLines.join('\n'), symbolicated: results.some((r) => r.changed), appFrame: appFrame?.trim() || null };
}

// --- Main reporting functions ---

export async function reportError(error) {
  if (process.env.NODE_ENV !== 'production') {
    console.log('[lex_luthor] error (dev):', error?.message || error);
    return;
  }

  try {
    const rawMessage = typeof error === 'string' ? error : (error?.message || 'Unknown');
    const url = typeof window !== 'undefined' ? window.location.href : undefined;

    if (isChunkLoadError(rawMessage)) return;
    // Crawlers (Applebot, SEBot-WA, Googlebot…) renderizan a medias y no son usuarios: no avisamos.
    if (isBot()) return;

    // Drop errors that originate from browser extensions / non-app sources, or
    // that carry no usable app stack frame — they aren't our code.
    const rawStack = error?.stack || '';
    if (isExtensionError(`${rawStack}\n${rawMessage}`)) return;
    if (hasNoUsableAppFrame(rawStack)) return;

    const cause = inferCause(rawMessage);
    if (cause === 'Stale JS (needs refresh)') return;
    if (await isFromStaleBuild(rawStack)) return;

    const route = identifyRouteContext(url);

    // Send to PostHog error tracking (groups by issue + stack; same filtered
    // errors that go to Slack — chunk/extension/no-frame noise already dropped).
    captureException(error, { route, cause, source: 'error_boundary' });

    let reportStack = error?.stack || 'N/A';
    let stackNote = '';
    let appFrameLine = '';

    // Try source map symbolication
    if (typeof window !== 'undefined' && reportStack !== 'N/A') {
      try {
        const result = await symbolicateStack(reportStack);
        reportStack = result.stack;
        if (result.appFrame) appFrameLine = `*App frame:* \`${result.appFrame}\`\n`;
        if (result.symbolicated) stackNote = '*Stack note:* Symbolicated with source maps.\n';
      } catch {
        // Fall back to raw stack
      }
    }

    const digestLine = error?.digest ? `*Digest:* ${error.digest}\n` : '';

    const formattedMessage =
      `*[lex_luthor] Error*\n\n` +
      `*Cause:* ${cause}\n` +
      `*Route:* ${route}\n` +
      `*URL:* ${url || 'N/A'}\n` +
      digestLine +
      `\n` +
      `*Error:* ${rawMessage}\n\n` +
      stackNote +
      appFrameLine +
      `*Stack:*\n\`\`\`${reportStack.split('\n').slice(0, 8).join('\n')}\`\`\``;

    await fetch(`${API_URL}/slack`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message: formattedMessage }),
    }).catch(() => {});
  } catch {
    // Silently fail
  }
}

// Simple version for non-Error contexts (window handlers). Mismos filtros que reportError: sin bots y
// sin errores que no traen un frame de la app (`stack` es el del Error original, si lo hay).
export function notifyError(message, stack) {
  if (process.env.NODE_ENV !== 'production') {
    console.log('[lex_luthor] error (dev):', message);
    return;
  }

  try {
    const url = typeof window !== 'undefined' ? window.location.href : 'N/A';
    const route = identifyRouteContext(url);
    const cause = inferCause(message);

    if (isChunkLoadError(message)) return;
    if (cause === 'Stale JS (needs refresh)') return;
    if (isBot()) return;
    if (hasNoUsableAppFrame(stack)) return;

    const formattedMessage =
      `*[lex_luthor] Error*\n\n` +
      `*Cause:* ${cause}\n` +
      `*Route:* ${route}\n` +
      `*URL:* ${url}\n\n` +
      `*Error:* ${message}`;

    fetch(`${API_URL}/slack`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message: formattedMessage }),
    }).catch(() => {});
  } catch {
    // Silently fail
  }
}
